import { useState, useEffect, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { inr, inrCompact } from "@/lib/format";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DownloadSimple, Sparkle, TrendUp, ArrowClockwise } from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";

const PRESETS = [
  { v: "today", l: "Today" },
  { v: "week", l: "This Week" },
  { v: "month", l: "This Month" },
  { v: "last_month", l: "Last Month" },
  { v: "last_3_months", l: "Last 3 Months" },
  { v: "all_time", l: "All Time" },
  { v: "custom", l: "Custom" },
];

const LOAN = "#0047AB";
const OB = "#EA580C";
const GREEN = "#16a34a";
const RED = "#dc2626";
const PIE_COLORS = ["#0047AB", "#EA580C", "#16a34a", "#a855f7", "#64748b"];

const Stat = ({ label, value, hint, accent, testid }) => (
  <div data-testid={testid} className="border border-zinc-200 bg-white rounded-sm p-4">
    <div className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</div>
    <div className="text-xl font-heading tracking-tight mt-1 tabular-nums" style={{ color: accent || "#18181b" }}>{value}</div>
    {hint && <div className="text-xs text-zinc-400 mt-0.5">{hint}</div>}
  </div>
);

const RollupTable = ({ title, subtitle, accent, rows, unitLabel, testid }) => {
  const list = rows || [];
  const max = list.reduce((m, r) => Math.max(m, r.amount || 0), 0) || 1;
  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b-2" style={{ borderColor: accent }}>
        <div className="text-[11px] uppercase tracking-widest" style={{ color: accent }}>{title}</div>
        <div className="text-xs text-zinc-400">{subtitle}</div>
      </div>
      <div className="px-5 py-3 space-y-2">
        {list.length === 0 ? <div className="text-sm text-zinc-400 py-8 text-center">No data in this period.</div>
          : list.map((r, i) => (
            <div key={i} className="space-y-1" data-testid={`${testid}-row`}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-700 truncate max-w-[60%]" title={r.name}>{i + 1}. {r.name}</span>
                <span className="tabular-nums font-medium text-zinc-900">{inrCompact(r.amount)}<span className="text-[11px] text-zinc-400 ml-1">· {r.count} {unitLabel}</span></span>
              </div>
              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(4, (r.amount / max) * 100)}%`, backgroundColor: accent }} /></div>
            </div>
          ))}
      </div>
    </div>
  );
};

function Row({ label, value, strong, color }) {
  return (
    <div className={`flex items-center justify-between py-2 border-b border-zinc-100 last:border-0 ${strong ? "font-semibold" : ""}`}>
      <span className={`text-sm ${strong ? "text-zinc-900" : "text-zinc-500"}`}>{label}</span>
      <span className="text-sm tabular-nums" style={{ color: color || (strong ? "#18181b" : "#3f3f46") }}>{value}</span>
    </div>
  );
}

export default function Reports() {
  const { can, isAdmin } = useAuth();
  const [preset, setPreset] = useState("month");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [rollups, setRollups] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [aiTips, setAiTips] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const params = useCallback(() => {
    const p = { preset };
    if (preset === "custom") { p.frm = frm; p.to = to; }
    return p;
  }, [preset, frm, to]);

  const load = useCallback(async () => {
    if (preset === "custom" && (!frm || !to)) return;
    try {
      const [s, p] = await Promise.all([
        api.get("/reports/summary", { params: params() }),
        api.get("/reports/pnl", { params: params() }),
      ]);
      setSummary(s.data); setPnl(p.data);
      api.get("/reports/rollups", { params: params() }).then((r) => setRollups(r.data)).catch(() => {});
    } catch { toast.error("Failed to load reports"); }
  }, [preset, frm, to, params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/reports/growth").then((r) => setGrowth(r.data.series || [])).catch(() => {}); }, []);

  const runAi = async (mode) => {
    setAiMode(mode); setAiLoading(true); setAiTips([]);
    try {
      const r = await api.post("/reports/ai-tips", { mode });
      setAiTips(r.data.tips || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI unavailable"); }
    finally { setAiLoading(false); }
  };

  const exportUrl = (type, format) => {
    const q = new URLSearchParams({ type, format, preset });
    if (preset === "custom") { q.set("frm", frm); q.set("to", to); }
    return `${API}/reports/export?${q.toString()}`;
  };

  const loan = summary?.loan || {};
  const ob = summary?.own_brand || {};
  const comp = summary?.company || {};
  const exp = summary?.expenses || {};
  const pLoan = pnl?.loan || {};
  const pOb = pnl?.own_brand || {};

  const expPie = Object.entries(exp.by_category || {}).map(([name, value]) => ({ name, value }));
  const profitBar = [
    { name: "Loan", profit: pLoan.net_loan_profit || 0 },
    { name: "Own Brand", profit: pOb.own_brand_profit || 0 },
  ];

  const ExportBtn = ({ type, label }) => can("export") || isAdmin() ? (
    <div className="flex items-center gap-2">
      <span className="text-xs text-zinc-500 w-24">{label}</span>
      <a href={exportUrl(type, "excel")} target="_blank" rel="noreferrer" data-testid={`export-${type}-excel`}
        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded-sm border border-zinc-200 text-zinc-600 hover:border-zinc-900 hover:text-zinc-900 transition-colors"><DownloadSimple size={13} /> Excel</a>
      <a href={exportUrl(type, "pdf")} target="_blank" rel="noreferrer" data-testid={`export-${type}-pdf`}
        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded-sm border border-zinc-200 text-zinc-600 hover:border-zinc-900 hover:text-zinc-900 transition-colors"><DownloadSimple size={13} /> PDF</a>
    </div>
  ) : null;

  return (
    <div data-testid="reports-page">
      <PageHeader overline="Reports & Analytics" title="Reports & Analytics"
        subtitle="Profit & loss across Loan and Own Brand, growth trends and AI insights — read-only." />

      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="flex gap-1 bg-white border border-zinc-200 rounded-sm p-1">
          {PRESETS.map((p) => (
            <button key={p.v} data-testid={`preset-${p.v}`} onClick={() => setPreset(p.v)}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${preset === p.v ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>{p.l}</button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" data-testid="report-from" value={frm} onChange={(e) => setFrm(e.target.value)} className="h-9 rounded-sm w-40" />
            <span className="text-zinc-400 text-sm">to</span>
            <Input type="date" data-testid="report-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-sm w-40" />
          </div>
        )}
        <Button variant="outline" size="sm" className="rounded-sm ml-auto" onClick={load} data-testid="report-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat testid="kpi-loan-disb" label="Loan Disbursed" value={inrCompact(loan.loan_amount)} hint={`${loan.count || 0} deals`} accent={LOAN} />
        <Stat testid="kpi-ob-sales" label="Own Brand Sales" value={inrCompact(ob.total_sale_amount)} hint={`${ob.count || 0} sales`} accent={OB} />
        <Stat testid="kpi-expenses" label="Total Expenses" value={inrCompact(exp.total)} hint="all categories" accent={RED} />
        <Stat testid="kpi-net-profit" label="Company Net Profit" value={inrCompact(comp.final_profit)} accent={(comp.final_profit || 0) >= 0 ? GREEN : RED} />
      </div>

      {/* P&L SECTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Loan P&L */}
        <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="pnl-loan">
          <div className="px-5 py-3 border-b-2" style={{ borderColor: LOAN }}>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: LOAN }}>Loan Business</div>
            <div className="font-heading text-lg tracking-tight">Loan P&L</div>
          </div>
          <div className="px-5 py-3">
            <Row label="Gross Lender Commission" value={inr(pLoan.gross_commission)} />
            <Row label="Agent Commission" value={`- ${inr(pLoan.agent_commission)}`} color={RED} />
            <Row label="Net Loan Profit" value={inr(pLoan.net_loan_profit)} strong color={(pLoan.net_loan_profit || 0) >= 0 ? GREEN : RED} />
          </div>
        </div>

        {/* Own Brand P&L */}
        <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="pnl-ob">
          <div className="px-5 py-3 border-b-2" style={{ borderColor: OB }}>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: OB }}>Own Brand Business</div>
            <div className="font-heading text-lg tracking-tight">Own Brand P&L</div>
          </div>
          <div className="px-5 py-3">
            <Row label="Amount Received" value={inr(pOb.amount_received)} />
            <Row label="Fixed Product Cost" value={`- ${inr(pOb.fixed_product_cost)}`} color={RED} />
            <Row label="Employee Commission (gross)" value={`- ${inr(pOb.gross_commission)}`} color={RED} />
            <Row label="OB Expenses (Mktg + Tech)" value={`- ${inr(pOb.ob_expenses)}`} color={RED} />
            <Row label="Own Brand Profit" value={inr(pOb.own_brand_profit)} strong color={(pOb.own_brand_profit || 0) >= 0 ? GREEN : RED} />
            <div className="text-[11px] text-zinc-400 mt-1">Basis: {pOb.revenue_basis}</div>
          </div>
        </div>

        {/* Combined P&L */}
        <div className="border-2 border-brand bg-white rounded-sm overflow-hidden" data-testid="pnl-company">
          <div className="px-5 py-3 border-b-2 border-brand bg-brand">
            <div className="text-[11px] uppercase tracking-widest text-indigo-200">Combined</div>
            <div className="font-heading text-lg tracking-tight text-white">Company P&L</div>
          </div>
          <div className="px-5 py-3">
            <Row label="Loan Profit" value={inr(comp.loan_profit)} color={LOAN} />
            <Row label="Own Brand Profit" value={inr(comp.own_brand_profit)} color={OB} />
            <Row label="Other Income" value={`+ ${inr(comp.other_income)}`} color={GREEN} />
            <Row label="Common Expenses" value={`- ${inr(comp.common_expenses)}`} color={RED} />
            <Row label="Final Net Profit" value={inr(comp.final_profit)} strong color={(comp.final_profit || 0) >= 0 ? GREEN : RED} />
          </div>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-zinc-200 bg-white rounded-sm p-5" data-testid="growth-chart">
          <div className="font-heading text-lg tracking-tight mb-4">6-Month Growth</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={growth} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => inrCompact(v)} width={70} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="disbursement" name="Loan Disbursed" stroke={LOAN} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="ob_sales" name="OB Sales" stroke={OB} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="profit" name="Net Profit" stroke={GREEN} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="profit-bar">
          <div className="font-heading text-lg tracking-tight mb-4">Profit by Business</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={profitBar} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => inrCompact(v)} width={70} />
              <Tooltip formatter={(v) => inr(v)} />
              <Bar dataKey="profit" name="Profit" radius={[3, 3, 0, 0]}>
                {profitBar.map((e, i) => <Cell key={i} fill={i === 0 ? LOAN : OB} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ROLLUPS — who disbursed / sold / spent most */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="rollups">
        <RollupTable title="Top Disbursers" subtitle="by team · loan amount" accent={LOAN} rows={rollups?.disbursers} unitLabel="deals" testid="rollup-disbursers" />
        <RollupTable title="Top Sellers" subtitle="by executive · amount received" accent={OB} rows={rollups?.sellers} unitLabel="sales" testid="rollup-sellers" />
        <RollupTable title="Where Money Went" subtitle="by expense category" accent={RED} rows={rollups?.spend_by_category} unitLabel="entries" testid="rollup-spend" />
      </div>

      {/* Expense breakdown + AI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="expense-pie">
          <div className="font-heading text-lg tracking-tight mb-4">Expense Breakdown</div>
          {expPie.length === 0 ? <div className="text-sm text-zinc-400 py-12 text-center">No expenses in this period.</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={expPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {expPie.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="ai-tips">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-lg tracking-tight flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-amber-500" /> AI Insights</div>
            <div className="flex gap-2">
              <Button size="sm" variant={aiMode === "growth" ? "default" : "outline"} className="rounded-sm" data-testid="ai-growth-btn" onClick={() => runAi("growth")} disabled={aiLoading}><TrendUp size={14} className="mr-1" /> Growth Tips</Button>
              <Button size="sm" variant={aiMode === "profit" ? "default" : "outline"} className="rounded-sm" data-testid="ai-profit-btn" onClick={() => runAi("profit")} disabled={aiLoading}><Sparkle size={14} className="mr-1" /> Profit Tips</Button>
            </div>
          </div>
          {aiLoading ? <div className="text-sm text-zinc-400 py-8 text-center animate-pulse">Analyzing your business data…</div>
            : aiTips.length === 0 ? <div className="text-sm text-zinc-400 py-8 text-center">Tap a button to generate read-only AI suggestions from your real data.</div>
              : <ul className="space-y-2" data-testid="ai-tips-list">
                {aiTips.map((t, i) => (
                  <li key={i} className="text-sm text-zinc-700 flex gap-2"><span className="text-amber-500 shrink-0">•</span><span>{t}</span></li>
                ))}
              </ul>}
        </div>
      </div>

      {/* Exports */}
      {(can("export") || isAdmin()) && (
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="exports">
          <div className="font-heading text-lg tracking-tight mb-4">Export Reports <span className="text-xs font-normal text-zinc-400">(current date range)</span></div>
          <div className="space-y-3">
            <ExportBtn type="loan" label="Loan Report" />
            <ExportBtn type="ob" label="Own Brand" />
            <ExportBtn type="expenses" label="Expenses" />
          </div>
        </div>
      )}
    </div>
  );
}
